import * as Sentry from '@sentry/react';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import ReactPlayer from 'react-player';
import { useDispatch } from 'react-redux';
import { Close, Delete, Edit, PlayCircleFilled, Mic as MicIcon } from '@mui/icons-material';
import {
  Button,
  FormControl,
  IconButton,
  Modal,
  Alert,
  Snackbar,
  styled as muiStyled,
} from '@mui/material';
import AudioRecorder from 'features/AudeoRecorderNew';
import COLOR from 'utils/constants/color';
import styled from 'styled-components';
import { AssignmentVideo } from 'features/forms/assignment';
import { DeleteModal } from 'components/Dialog';
import { filter, find, get, has, map } from 'lodash';
import {
  handleCreateInstructions,
  handleDeleteInstructions,
  handleUpdateInstructions,
  handleListInstructionByClass,
} from 'store/instructions/thunks';
import { handleCreateVocabulary } from 'store/vocabulary/thunks';
import { Row, Select, Tag } from 'antd';
import { useBookOrder, useUserAllowedBooks } from 'store/books/selectors';
import { useCurrentClass } from 'store/classes/selectors';
import { useCurrentUser } from 'store/users/selectors';
import { useInstructions } from 'store/instructions/selectors';
import { useUserClasses, useUsersByClass } from 'store/userClasses/selectors';
import Loading from '../../../components/Loading';
import { useResources } from 'store/resources/selectors';
import { isVideo } from 'utils/lib/ebookHelper';
import { v4 } from 'uuid';
import moment from 'moment-timezone';
import useGetStudentFromClass from 'components/hooks/useGetStudentFromClass/useGetStudentFromClass';
import useNotificationInstrucation from './hooks/useNotificationInstrucation';
import usePopUpAtOnce from 'components/hooks/usePopUpAtOnce';
import usePermissionForUser from 'components/hooks/permissions/usePermissionForUser';
import TranscriptWithAudio from 'components/UI/transcriptWithAudio/transcriptWithAudio';
import { useLocation, useNavigate } from 'react-router-dom';
import useBookAvailabilityCheck from 'components/hooks/books/useBookAvailabilityCheck';
import SentryWrap from 'components/Sentry';

//  ----------------------------------------------------------------------------
//  Dependencies
//  ----------------------------------------------------------------------------

//  -- Components ---------------------

//  -- Constants ----------------------

const { Option } = Select;
//  ----------------------------------------------------------------------------
//  Styles
//  ----------------------------------------------------------------------------
const Wrapper = styled.div`
  background-color: ${COLOR.green800};
  border-bottom-right-radius: 24px;
  border-top-left-radius: 24px;
  border-top-right-radius: 24px;
  padding: 12px 12px 8px 12px;
  position: relative;
  width: 100%;

  &:last-child {
    margin-bottom: 0px;
  }
`;
const ModalContent = styled.div`
  left: 50%;
  max-width: 544px;
  min-width: 450px;
  padding: 0px 24px;
  position: absolute;
  border-radius: 10px;
  top: 50%;
  transform: translate(-50%, -50%);
  background-color: #fff;
  padding: 10px;

  @media (max-width: 425px) {
    min-width: 320px;
  }
`;

const AllModalContent = styled.div`
  left: 50%;
  max-width: 544px;
  min-width: 450px;
  padding: 0px 24px;
  position: relative;
  border-radius: 10px;
  top: 50%;
  transform: translate(-50%, -50%);
  background-color: #fff;
  padding: 10px;
  position: relative;
  @media (max-width: 560px) {
    min-width: 95%;
    margin-right: 20px;
  }
`;

// const ModalContents = styled.div`
//   left: 50%;
//   max-width: 544px;
//   min-width: 450px;
//   padding: 0px 24px;
//   position: absolute;
//   border-radius: 10px;
//   top: 50%;
//   transform: translate(-50%, -50%);
//   background-color: #fff;
//   padding: 10px;
//   max-height:550px;
//   overflow:scroll;
// `;
const ModalConfigArea = styled.div`
  max-height: 75vh;
  overflow: auto;
  overflow-x: hidden;
  margin-top: 10px;
  padding: 10px;
`;
const ModalConfigAreaSection = styled.div`
  padding: 10px;
  video {
    height: inherit !important;
    margin-top: 10px;
  }
`;
const ActionText = styled.p`
  font-size: 14px;
  color: white;
  font-weight: 500;
`;
const InstButton = styled(Button)`
  border-radius: 8px 8px 0 0 !important;
  box-shadow: none !important;
  font-size: 10px;
`;

const SEEN_INSTRUCTIONS_KEY = 'seenInstructions';

const hasSeenInstructionLocally = (instructionId, notificationModified) => {
  try {
    const seenInstructions = localStorage.getItem(SEEN_INSTRUCTIONS_KEY);
    if (!seenInstructions) return false;
    
    const seenMap = JSON.parse(seenInstructions);
    const seenData = seenMap[instructionId];
    
    if (!seenData) return false;
    
    if (!notificationModified) return true;
    return seenData.timestamp && new Date(seenData.timestamp) >= new Date(notificationModified);
  } catch (error) {
    Sentry.captureException(error);
    return false;
  }
};

const markInstructionAsSeenLocally = (instructionId, notificationModified) => {
  try {
    const seenInstructions = localStorage.getItem(SEEN_INSTRUCTIONS_KEY);
    const seenMap = seenInstructions ? JSON.parse(seenInstructions) : {};
    
    seenMap[instructionId] = {
      timestamp: notificationModified || new Date().toISOString(),
    };
    
    localStorage.setItem(SEEN_INSTRUCTIONS_KEY, JSON.stringify(seenMap));
  } catch (error) {
    Sentry.captureException(error);
  }
};

//  ----------------------------------------------------------------------------
//  Component
//  ----------------------------------------------------------------------------
//
//  Instructions
//
//  -- Description
//  TODO
//
//  -- Props
//  ...
//
//  -- State
//  ...
//
//  -- Methods
//  ...
//
//  -- Usage
//  ...
//

const StyledRoot = muiStyled('div')(({ theme }) => ({
  marginBottom: '25px',
  width: '300px',
}));

const StyledRootDif = muiStyled('div')(({ theme }) => ({
  width: '200px',
}));

const StyledRootDifFull = muiStyled('div')(({ theme }) => ({
  width: '100%',
}));

const StyledFormControl = muiStyled(FormControl)(({ theme }) => ({
  margin: '2px',
  minWidth: 120,
}));

const StyledButton = muiStyled(Button)(({ theme }) => ({
  backgroundColor: 'rgb(0, 43, 112)',
  color: 'white !important',
  '&:hover': {
    backgroundColor: 'rgb(0, 43, 112)',
  },
  '&.MuiButton-sizeSmall': {
    width: '55px',
    height: '25px',
  },
}));

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

const MENU = [
  { id: 'audio_instruction', value: 'Audio instruction' },
  { id: 'video_instruction', value: 'Video instruction' },
];

const Instructions = ({
  customClass,
  pageId,
  bookId,
  setInstModal,
  instModal,
  from = '',
  setCheckInstructionDataForStudent = () => {},
  closeToolsDropdown = () => {}
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const currentUser = useCurrentUser();
  const currentClass = useCurrentClass();
  const instructions = useInstructions();
  const userClasses = useUserClasses();
  const [instruction, setIntruction] = useState(null);
  const [selectableClass, setSelectableClass] = useState([]);
  const [openRecorder, setRecorder] = useState(false);
  const [playerModal, setPlayerModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [alertSakeBar, setAlertSakeBar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });
  const [deletePageModal, setDeletePageModal] = useState(false);
  const [deleteLoader, setDeleteLoader] = useState(false);
  const dispatch = useDispatch();
  const [addVocabData, setAddVocabData] = useState({});
  const [vocabularyAI, setVocabularyAI] = useState();
  const [buttonDisabled, setButtonDisabled] = useState(false);
  const [videoInstruction, setVideoInstuction] = useState({
    duration: 60,
    video: '',
  });
  const [dataTab, setDataTab] = useState(MENU[0].id);
  const [isAudioPlaying, setIsAudioPlaying] = useState(false);
  const [isVideoRecording, setVideoRecording] = useState(false);
  const audioPlayerRef = useRef(null);
  const videoPlayerRef = useRef(null);
  const allowed_books = useUserAllowedBooks();
  const bookOrder = useBookOrder();
  const resources = useResources();
  const { sendNotification, isCurrentStudentSeenInstruction, updateUserSeen,sendNotificationPopUp, currentNotificationInstruction } =
    useNotificationInstrucation(get(instruction, 'id'));
  const { classUsers } = useGetStudentFromClass();
  const usersByClass = useUsersByClass();
      const {checkPopUpAtOnce, updatePopUpAtOnce} = usePopUpAtOnce();
  const checkBookAvailability = useBookAvailabilityCheck(bookId, 'Instruction');

  const navigationToMenu = (id) => {
    if (dataTab === MENU[0].id) {
      if (isAudioPlaying) {
        return;
      } else {
        setDataTab(id);
      }
    } else {
      setDataTab(id);
    }
  };

  const notificationModified = useMemo(
    () => get(currentNotificationInstruction, 'modified'),
    [currentNotificationInstruction]
  );

  const showInstructionNotifyMsgOnStudent = useMemo(() => {
    return (
      !isCurrentStudentSeenInstruction &&
      has(instruction, 'id') &&
      get(currentUser, 'role') === 'student'
    );
  }, [isCurrentStudentSeenInstruction, instruction?.id, currentUser?.role]);

  useEffect(() => {
    if (
      !isCurrentStudentSeenInstruction &&
      has(instruction, 'id') &&
      get(currentUser, 'role') === 'student' &&
      !hasSeenInstructionLocally(instruction.id, notificationModified)
    ) {
      setPlayerModal(true);
    }
    return () => {
      updateUserSeen();
    };
  }, [isCurrentStudentSeenInstruction, instruction?.id, notificationModified]);

  useEffect(() => {
    if (instModal) {
      if (has(instruction, 'id') && get(currentUser, 'role') === 'student') {
        setPlayerModal(true);
      }
      if (get(currentUser, 'role') === 'teacher') {
        setRecorder(true);
      }
    }
  }, [instModal]);

  useEffect(() => {
    try {
      const searchParams = new URLSearchParams(location.search);
      if (searchParams.get('openInstruction') === 'true' && has(instruction, 'id') && get(currentUser, 'role') === 'student') {
        searchParams.delete('openInstruction');
        const newSearch = searchParams.toString();
        const newUrl = location.pathname + (newSearch ? '?' + newSearch : '') + location.hash;
        navigate(newUrl, { replace: true });
        setPlayerModal(true);
      }
    } catch (error) {
      Sentry.captureException(error);
    }
  }, [location.search, instruction?.id, currentUser?.role]);

  useEffect(() => {
    if (has(instruction, 'id') && get(currentUser, 'role') === 'student') {
      setCheckInstructionDataForStudent(true);
    } else {
      setCheckInstructionDataForStudent(false);
    }
  }, [instruction]);
  
  useEffect(() => {
    try {
      if (playerModal && get(currentUser, 'role') === 'student' && get(currentClass, 'id')) {
        dispatch(handleListInstructionByClass(get(currentClass, 'id')));
      }
    } catch (error) {
      Sentry.captureException(error);
    }
  }, [playerModal, currentUser?.role, currentClass?.id]);

  useEffect(() => {
    const instr = filter(
      instructions,
      (k) =>
        k.bookId == bookId &&
        k.pageId == pageId &&
        find(k.classes, (c) => c == get(currentClass, 'id')),
    );
    const inst_data = instr[0];
    if (inst_data) {
      setIntruction(inst_data);
      setVideoInstuction({ video: inst_data?.video_source });
    } else {
      const cls = [];
      if (currentClass && currentClass.id) {
        cls.push(currentClass.id);
      }
      setIntruction({
        source: null,
        classes: cls,
        createdBy: get(currentUser, 'id'),
        bookId: bookId,
        pageId: pageId,
        video_source: null,
      });
      setVideoInstuction({});
    }
  }, [pageId, bookId, instructions, currentClass]);

  useEffect(() => {
    setTimeout(() => {
      if (addVocabData.word == undefined) {
      } else {
        addVocabulary();
      }
    }, 2000);
  }, [vocabularyAI]);

  useEffect(() => {
    try {
      if (userClasses && Array.isArray(userClasses) && userClasses.length > 0) {
        const extra_books = allowed_books.extra_books;
        const group = bookOrder.filter(({ order }) => (order || []).some((k) => k == bookId));
        const filteredClasses = userClasses.filter((x) => {
          if (x.archived) {
            return false;
          }
          const archivedGroup = get(x, 'books.archived_group', []);
          const books_group = get(x, 'books.books_group', []);
          const is_in_group = group.some((k) => books_group.includes(k.key));
          const in_in_archive = group.some((k) => archivedGroup.includes(k.key));
          if (is_in_group) {
            if (in_in_archive) {
              return false;
            }
            return true;
          } else if (extra_books.some((k) => k == bookId)) {
            return true;
          } else if (allowed_books.other_permissions.resourses && isVideo(bookId, resources)) {
            return true;
          }
          return false;
        });
        const sclass = filteredClasses.map((e) => ({
          ...e,
          displayName: `${e.displayName} (${e.code})`,
        }));
        setSelectableClass(sclass);
      }
    } catch (error) {
      console.log(error, 'error,quiz');
    }
  }, [userClasses, resources, bookOrder, bookId]);

  const onCloseModal = () => {
    updateUserSeen();
    if (has(instruction, 'id') && get(currentUser, 'role') === 'student') {
      markInstructionAsSeenLocally(instruction.id, notificationModified);
    }
    setPlayerModal(false);
    setRecorder(false);
    setInstModal(false);
    updatePopUpAtOnce(false);
  };
  const onCreateUpdate = async () => {
    if (!checkBookAvailability()) {
      return;
    }
    
    const selectedClassIds = get(instruction, 'classes', []);
    const selectedClasses = selectableClass?.filter((cls) => selectedClassIds.includes(cls.id)) || [];
    
    const filterClassUsers = [];
    selectedClassIds.forEach((classId) => {
      const classUsers = get(usersByClass, classId, []);
      const students = classUsers
        .filter((user) => user?.role === 'student' && user?.active === true)
        .map((user) => user.id);
      filterClassUsers.push(...students);
    });
    
    if (has(instruction, 'id')) {
      setLoading(true);
      instruction.video_source = videoInstruction?.video;
      const d = await dispatch(handleUpdateInstructions(instruction.id, instruction));
      //call notification service
      if (has(d, 'id')) {
        const notificationData = {
          id: v4(),
          classId: get(currentClass, 'id', ''),
          displayName: 'Instruction',
          senderId: get(currentUser, 'id', ''),
          receiversId: filterClassUsers,
          modified: moment().toISOString(),
          data: {
            chapter: bookId,
            page: pageId,
            id: d?.id,
            classes: selectedClasses?.map((element) => {
              return {
                id: element?.id,
                users: element?.users ? element?.users : null,
              };
            }),
            urlPath: window.location.pathname + window.location.search + window.location.hash,
          },
          message: `Your teacher updated ${MENU?.find((x) => x.id === dataTab)?.value}`,
          type: 'instruction',
          userSeen: [],
        };
        sendNotification(notificationData);
        setAlertSakeBar({
          open: true,
          message: 'Updated instruction!',
          severity: 'success',
        });
        onCloseModal();
      } else {
        setAlertSakeBar({
          open: true,
          message: 'Failed to update instruction!',
          severity: 'error',
        });
      }
      setLoading(false);
    } else {
      setLoading(true);
      instruction.video_source = videoInstruction?.video;
      const d = await dispatch(handleCreateInstructions(instruction));
      if (has(d, 'id')) {
        //call notification service
        const notificationData = {
          id: v4(),
          classId: get(currentClass, 'id', ''),
          displayName: 'Instruction',
          senderId: get(currentUser, 'id', ''),
          receiversId: filterClassUsers,
          modified: moment().toISOString(),
          data: {
            chapter: bookId,
            page: pageId,
            id: d?.id,
            classes: selectedClasses?.map((element) => {
              return {
                id: element?.id,
                users: element?.users ? element?.users : null,
              };
            }),
            urlPath: window.location.pathname + window.location.search + window.location.hash,
          },
          message: `Your teacher created ${MENU?.find((x) => x.id === dataTab)?.value}`,
          type: 'instruction',
          userSeen: [],
        };
        sendNotification(notificationData);
        setAlertSakeBar({
          open: true,
          message: 'Created instruction!',
          severity: 'success',
        });
        onCloseModal();
      } else {
        setAlertSakeBar({
          open: true,
          message: 'Failed to create instruction!',
          severity: 'error',
        });
      }
      setLoading(false);
    }
  };
  const onRemove = async () => {
    setDeleteLoader(true);
    const d = await dispatch(handleDeleteInstructions(instruction.id));
    if (has(d, 'id')) {
      setAlertSakeBar({
        open: true,
        message: 'Deleted instruction!',
        severity: 'success',
      });
    } else {
      setAlertSakeBar({
        open: true,
        message: 'Failed to delete instruction!',
        severity: 'error',
      });
    }
    setDeletePageModal(false);
    setDeleteLoader(false);
    setVideoInstuction({});
    onCloseModal();
  };
  const closeAlert = () => {
    setAlertSakeBar({ open: false, message: '', severity: 'success' });
  };

  const addVocabulary = () => {
    const dataVocab = {
      book_id: bookId,
      page: pageId,
      chapter: bookId,
      created_by: currentUser?.role,
      user_id: get(currentUser, 'id', ''),
      class_id: get(currentClass, 'id', ''),
      data: {
        word: addVocabData.word,
        ...vocabularyAI,
      },
    };
    const result = dispatch(handleCreateVocabulary(dataVocab));
    setAlertSakeBar({
      open: true,
      message: 'Created successfully',
      severity: 'success',
    });
    if (result) {
      setAddVocabData({});
      setVocabularyAI({});
    }
  };

  const handleVideoUploader = (value, key) => {
    try {
      setVideoInstuction((prev) => ({ ...prev, [key]: value }));
      if (key === 'video') {
        setIntruction((prev) => ({ ...prev, video_source: value }));
      }
    } catch (error) {
      Sentry.captureException(`226 Error in handleVideoUploader: ${error}`);
      console.log(error);
    }
  };

  const disabledCreateAndUpdate = (value) => {
    setButtonDisabled(value);
  };

  const checkDisable =
    dataTab === MENU[0].id ? !get(instruction, 'source') : !get(instruction, 'video_source');

  const handleDeleteAudioVideo = (type) => {
    if (type === 'audio') {
      setIntruction((prev) => ({ ...prev, source: null }));
    } else {
      setVideoInstuction((prev) => ({ ...prev, video: null }));
      setIntruction((prev) => ({ ...prev, video_source: null }));
    }
  };

  const handleAudioPlay = () => {
    try {
      const videoPlayer = videoPlayerRef.current;
      if (videoPlayer) {
        const internalPlayer = videoPlayer.getInternalPlayer();
        if (internalPlayer && typeof internalPlayer.pause === 'function') {
          internalPlayer.pause();
        }
      }
    } catch (error) {
      Sentry.captureException(`226 Error in handleAudioPlay: ${error}`);
    }
};

  // Handle when video starts playing
  const handleVideoPlay = () => {
    try {
      const audioPlayer = audioPlayerRef.current;
      if (audioPlayer) {
        const internalPlayer = audioPlayer.getInternalPlayer();
        if (internalPlayer && typeof internalPlayer.pause === 'function') {
          internalPlayer.pause();
        }
      }
    } catch (error) {
      Sentry.captureException(`226 Error in handleVideoPlay: ${error}`);
    }
  };


  return (
    <SentryWrap>
      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        open={alertSakeBar.open}
        autoHideDuration={3000}
        onClose={closeAlert}
      >
        <Alert
          onClose={() => {
            setAlertSakeBar({ open: false, message: '' });
          }}
          severity={alertSakeBar.severity}
        >
          <strong>ALERT:</strong> {alertSakeBar.message}
        </Alert>
      </Snackbar>
      <DeleteModal
        deleteToggle={deletePageModal}
        setDeleteToggle={setDeletePageModal}
        loading={deleteLoader}
        message={`Are you sure you want to remove the instruction on this page?`}
        onDelete={onRemove}
      />
      <Row
        style={{ minWidth: 'unset', height: customClass ? 'undefined' : '36px' }}
        className={`scheduledWebCstm scheduledWeb ${from + '-mic'} ${customClass ? 'resourceMicSection' : ''}`}
      >
        {has(instruction, 'id') && get(currentUser, 'role') === 'student' ? (
          <StyledButton
            size="small"
            color="primary"
            variant={from === 'balcon' ? '' : 'contained'}
            onClick={() =>{
                closeToolsDropdown();
                if(checkPopUpAtOnce()) return;
                updatePopUpAtOnce(true);
                setPlayerModal(true)
            }}
            style={{ marginRight: '5px' }}
          >
            PLAY{' '}
            <PlayCircleFilled
              color="error"
              style={{ marginLeft: 6, backgroundColor: '#fff', borderRadius: '50%' }}
            />
          </StyledButton>
        ) : null}
        {get(currentUser, 'role') === 'teacher' ? (
          <StyledButton
            size="small"
            color="primary"
            variant={from === 'balcon' ? '' : 'contained'}
            onClick={() => {
              closeToolsDropdown();
              if(checkPopUpAtOnce()) return;
              updatePopUpAtOnce(true);
              setRecorder(true)
            }}
          >
            {has(instruction, 'id') ? (
              <PlayCircleFilled
                color="error"
                style={{ backgroundColor: '#fff', borderRadius: '50%' }}
              />
            ) : (
              <MicIcon color="error" style={{ backgroundColor: '#fff', borderRadius: '50%' }} />
            )}{' '}
            {has(instruction, 'id') && <Edit style={{ marginLeft: 6, color: '#fff' }} />}
          </StyledButton>
        ) : null}
      </Row>

      <Modal
        disableEscapeKeyDown
        disableBackdropClick
        open={openRecorder}
        onClose={onCloseModal}
        style={{ height: '100%', width: '100%', backgroundColor: 'transparent' }}
      >
        <AllModalContent>
          <Row style={{ justifyContent: 'space-between' }}>
            <h3 style={{ color: 'black', marginTop: 5, marginLeft: 5, marginBottom: 0 }}>
              Instruction
            </h3>
            <IconButton
              onClick={() => {
                // if (isVideoRecording || isAudioPlaying) {
                //   return;
                // }
                if(buttonDisabled){
                  return;
                }
                onCloseModal(); // Allow closing if nothing is recording/playing
              }}
              style={{ padding: 0, marginRight: 10 }}
            >
              <Close color="secondary" />
            </IconButton>
          </Row>
          <Row className="instructDelete">
            {has(instruction, 'id') ? (
              <IconButton onClick={() => setDeletePageModal(true)}>
                <Delete color="error" />
              </IconButton>
            ) : null}
          </Row>
          <MenuWrapper>
            {MENU?.map((x) => (
              <MenuBtn
                style={{
                  borderBottom:
                    dataTab === x?.id ? '5px solid rgb(28,49,115)' : '5px solid lightGrey',

                  color: dataTab === x?.id ? 'rgb(28,49,115)' : 'black',
                }}
                key={x.id}
                onClick={() => navigationToMenu(x.id)}
                disabled={buttonDisabled}
              >
                {x.value}
              </MenuBtn>
            ))}
          </MenuWrapper>

          <ModalConfigArea>
            {dataTab === MENU[0].id && (
              <AudioRecorder
                hasSaveButton={true}
                source={get(instruction, 'source')}
                message={`To begin your recording click the "Record" button below.`}
                handleAudio={(url) => {
                  setIntruction({
                    ...instruction,
                    source: url,
                  });
                }}
                isRecordingStatus={(v) => (v ? setIsAudioPlaying(true) : setIsAudioPlaying(false))}
                handleDeleteAudioVideo={handleDeleteAudioVideo}
                from={'worksheet'}
                setButtonDisabled={setButtonDisabled}
              />
            )}
            {dataTab === MENU[1].id && (
              <AssignmentVideo
                states={{
                  index: 0,
                  question: videoInstruction,
                  from: 'instruction',
                  isVideoRecording,
                  setVideoRecording,
                }}
                funcObj={{
                  onChangeQuestionData: handleVideoUploader,
                  setButtonDisabled: disabledCreateAndUpdate,
                  handleDeleteAudioVideo: handleDeleteAudioVideo,
                }}
              />
            )}
            <StyledFormControl variant="outlined" className="indentSelectUpr">
              <Select
                size={'large'}
                className="indentSelect"
                mode="multiple"
                allowClear
                style={{ width: '100%', marginTop: 10, marginBottom: 10 }}
                placeholder="Select Classes"
                value={get(instruction, 'classes', [])}
                onChange={(selectedValues) => {
                  setIntruction({ ...instruction, classes: selectedValues });
                }}
                showSearch={false}
                dropdownStyle={{ zIndex: 9999 }}
                tagRender={(props) => {
                  const { value, closable, onClose } = props;
                  const displayName = get(
                    find(selectableClass, { id: value }),
                    'displayName',
                    value,
                  );

                  return (
                    <Tag closable={closable} onClose={onClose} style={{ marginRight: 4 }}>
                      {displayName}
                    </Tag>
                  );
                }}
              >
                {map(selectableClass, (x) => (
                  <Option key={x.id} value={x.id}>
                    {x.displayName}
                  </Option>
                ))}
              </Select>
            </StyledFormControl>
            <StyledButton
              disabled={
                get(instruction, 'classes.length', 0) == 0 ||
                loading ||
                checkDisable ||
                isAudioPlaying ||
                buttonDisabled
              }
              onClick={onCreateUpdate}
              fullWidth
              variant="contained"
              color="primary"
            >
              {loading && <Loading size={15} white />}{' '}
              {has(instruction, 'id') ? 'Update' : 'Create'}
            </StyledButton>
          </ModalConfigArea>
        </AllModalContent>
      </Modal>

      <Modal
        disableEscapeKeyDown
        disableBackdropClick
        open={playerModal}
        onClose={onCloseModal}
        style={{ height: '100%', width: '100%', backgroundColor: 'transparent' }}
      >
        <ModalContent>
          <Row style={{ justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <h3 style={{ color: 'black', marginTop: 5, marginBottom: 0, display: 'inline' }}>
                Instruction
              </h3>
              {showInstructionNotifyMsgOnStudent && (
                <p style={{ color: 'blue', marginTop: 5, marginLeft: 5, marginBottom: 0 }}>
                  Your teacher has added/updated the instructions for this page
                </p>
              )}
            </div>
            <IconButton onClick={onCloseModal} style={{ padding: 0, marginLeft: 10 }}>
              <Close color="secondary" />
            </IconButton>
          </Row>
          <ModalConfigAreaSection>
            {get(instruction, 'source') && get(instruction, 'source') !== '' && get(instruction, 'source') !== null && (
              <TranscriptWithAudio audioUrl={get(instruction, 'source')}>
                <ReactPlayer
                  url={get(instruction, 'source')}
                  height="40px"
                  width="100%"
                  controls
                  playsinline={true}
                  pip={false}
                  ref={audioPlayerRef}
                  onPlay={handleAudioPlay}
                  config={{
                    file: {
                      attributes: {
                        controlsList: "nodownload",
                        playsInline: true,
                        disablePictureInPicture: true,
                        webkitPlaysinline: true,
                        preload: 'auto',
                        disableRemotePlayback: true,
                      },
                      forceAudio: true,
                    },
                  }}
                />
              </TranscriptWithAudio>
            )}

            {get(instruction, 'video_source') && get(instruction, 'video_source') !== '' && get(instruction, 'video_source') !== null && (
              <ReactPlayer
                url={get(instruction, 'video_source')}
                width="100%"
                height="inherit"
                controls
                playsinline={true}
                pip={false}
                ref={videoPlayerRef}
                onPlay={handleVideoPlay}
                config={{
                  file: {
                    attributes: {
                      controlsList: 'nodownload',
                      playsInline: true,
                      disablePictureInPicture: true,
                      webkitPlaysinline: true,
                      preload: 'auto',
                      disableRemotePlayback: true,
                    },
                  },
                }}
              />
            )}
          </ModalConfigAreaSection> 
        </ModalContent>
      </Modal>
    </SentryWrap>
  );
};

export default Instructions;

const MenuWrapper = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
  min-height: 80px;
  border-radius: 12px;
  align-items: center;
  position: relative;
  padding: 2px 0px;
  flex-wrap: wrap;
  justify-content: center;
  width: 100%;
`;

const MenuBtn = styled.button`
  background-color: white;
  border-top: none;
  border-left: none;
  border-right: none;
  cursor: pointer;
  font-size: 16px;
  font-weight: 600;
  outline: none;
  transition: all 300ms ease;
  padding: 2px 16px 10px 16px;
  min-width: 150px;
  margin-right: 2px;
  margin-left: 2px;
  @media (max-width: 992px) {
    margin-top: 10px;
  }
`;
